import React, { useState } from "react";
import axios from "axios";
import { locations, propertyTypes, conditions, estimateRent } from "../estimationData";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { ArrowRight, Home, TrendingUp, ShieldCheck, RotateCcw } from "lucide-react";

const euro = (n) => n.toLocaleString("fr-FR") + " €";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EstimationSimulator({ embedded = false }) {
  const [form, setForm] = useState({
    location: "",
    type: "",
    condition: "correct",
    surface: "",
    furnished: false,
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleEstimate = () => {
    if (!form.location || !form.type || !form.surface) {
      setError("Merci de renseigner l'adresse, le type de bien et la surface.");
      return;
    }
    setError("");
    const r = estimateRent({ ...form, surface: Number(form.surface) });
    setResult(r);
    if (r) {
      const { location, type, condition, surface, furnished } = form;
      axios.post(`${API}/loyaris/estimations`, {
        location, type, condition, surface: Number(surface), furnished, monthly: r.monthly, low: r.low, high: r.high,
      }).catch(() => {});
    }
    setTimeout(() => {
      document.getElementById("resultat-estimation")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const reset = () => {
    setForm({ location: "", type: "", condition: "correct", surface: "", furnished: false });
    setResult(null);
    setError("");
  };

  return (
    <section id="estimateur-loyer" className={embedded ? "bg-[#EFEAE1] py-20 lg:py-24" : "bg-[#F4F0E9] pt-32 pb-20 lg:pt-40 lg:pb-24"}>
      <div className="mx-auto max-w-[1100px] px-5 lg:px-8">
        <div className="max-w-2xl">
          <p className="mb-3 flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#B08D57]">
            <span className="h-px w-8 bg-[#B08D57]" /> Estimation gratuite
          </p>
          {embedded ? (
            <h2 className="font-serifDisplay text-[34px] leading-tight text-[#26241F] lg:text-[46px]">
              Estimez votre loyer et vos frais de gestion.
            </h2>
          ) : (
            <h1 className="font-serifDisplay text-[38px] leading-tight text-[#26241F] lg:text-[54px]">
              Estimez votre loyer et vos honoraires.
            </h1>
          )}
          <p className="mt-4 text-[16px] leading-relaxed text-[#6e6a62]">
            Renseignez l'adresse et les caractéristiques de votre bien pour obtenir une estimation
            indicative du loyer mensuel et du coût de notre gestion.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_0.95fr]">
          {/* Form */}
          <div className="rounded-3xl border border-[#e0d8c8] bg-[#FBF9F5] p-7 lg:p-9">
            <div className="space-y-5">
              <div>
                <label className="mb-2 block text-[13px] font-semibold text-[#403d35]">
                  Adresse du bien
                </label>
                <Select value={form.location} onValueChange={(v) => update("location", v)}>
                  <SelectTrigger className="h-12 border-[#ded5c4] bg-white text-[15px]">
                    <SelectValue placeholder="Sélectionnez un arrondissement / une ville" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {locations.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-[13px] font-semibold text-[#403d35]">
                    Type de bien
                  </label>
                  <Select value={form.type} onValueChange={(v) => update("type", v)}>
                    <SelectTrigger className="h-12 border-[#ded5c4] bg-white text-[15px]">
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-2 block text-[13px] font-semibold text-[#403d35]">
                    Surface (m²)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.surface}
                    onChange={(e) => update("surface", e.target.value)}
                    placeholder="Ex : 45"
                    className="h-12 w-full rounded-md border border-[#ded5c4] bg-white px-4 text-[15px] text-[#26241F] outline-none transition-colors focus:border-[#B08D57]"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-[13px] font-semibold text-[#403d35]">
                  État du logement
                </label>
                <Select value={form.condition} onValueChange={(v) => update("condition", v)}>
                  <SelectTrigger className="h-12 border-[#ded5c4] bg-white text-[15px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {conditions.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-[#e6ddcd] bg-white px-4 py-3.5">
                <div>
                  <div className="text-[15px] font-semibold text-[#26241F]">Logement meublé</div>
                  <div className="text-[13px] text-[#9a9488]">Un bien meublé se loue en moyenne plus cher</div>
                </div>
                <Switch
                  checked={form.furnished}
                  onCheckedChange={(v) => update("furnished", v)}
                  className="data-[state=checked]:bg-[#B08D57]"
                />
              </div>

              {error && <p className="text-[13px] font-medium text-[#b4472e]">{error}</p>}

              <div className="flex flex-wrap gap-3 pt-1">
                <button
                  onClick={handleEstimate}
                  className="group flex flex-1 items-center justify-center gap-2 rounded-md bg-[#26241F] px-6 py-4 text-[15px] font-semibold text-white transition-colors hover:bg-[#B08D57] hover:text-[#26241F]"
                >
                  Estimer mon loyer
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                {result && (
                  <button
                    onClick={reset}
                    className="flex items-center justify-center gap-2 rounded-md border border-[#ded5c4] px-5 py-4 text-[15px] font-semibold text-[#26241F] transition-colors hover:bg-[#efe8db]"
                  >
                    <RotateCcw className="h-4 w-4" /> Réinitialiser
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Result */}
          <div id="resultat-estimation">
            {!result ? (
              <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-dashed border-[#cfc7b7] bg-[#efe9de] p-10 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#e2d9c8] text-[#B08D57]">
                  <Home className="h-7 w-7" />
                </span>
                <h3 className="mt-5 text-[18px] font-semibold text-[#26241F]">
                  Votre estimation apparaîtra ici
                </h3>
                <p className="mt-2 max-w-xs text-[14px] text-[#6e6a62]">
                  Complétez le formulaire pour découvrir le loyer estimé et le coût de nos services.
                </p>
              </div>
            ) : (
              <div className="rounded-3xl bg-[#26241F] p-7 text-white lg:p-9">
                <div className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.18em] text-[#B08D57]">
                  <TrendingUp className="h-4 w-4" /> Loyer mensuel estimé
                </div>
                <div className="mt-3 font-serifDisplay text-[52px] leading-none">
                  {euro(result.monthly)}
                  <span className="text-[18px] font-normal text-white/60"> /mois</span>
                </div>
                <div className="mt-2 text-[14px] text-white/70">
                  Fourchette indicative : {euro(result.low)} – {euro(result.high)} · env. {result.perM2} €/m²
                </div>

                <div className="mt-7 space-y-3">
                  <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-white/50">
                    Nos honoraires de gestion
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3.5">
                    <div>
                      <div className="text-[15px] font-semibold">Formule Liberté · 3,6 %</div>
                      <div className="text-[13px] text-white/60">Gestion courante</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[18px] font-bold text-[#B08D57]">{euro(result.feesInitial)}</div>
                      <div className="text-[12px] text-white/50">/mois</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-[#B08D57]/40 bg-[#B08D57]/10 px-4 py-3.5">
                    <div>
                      <div className="text-[15px] font-semibold">Formule Sérénité · 4,20 %</div>
                      <div className="text-[13px] text-white/60">Suivi renforcé</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[18px] font-bold text-[#B08D57]">{euro(result.feesPrestige)}</div>
                      <div className="text-[12px] text-white/50">/mois</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3.5">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-[#B08D57]" />
                      <div className="text-[15px] font-semibold">Option GLI · 2,1 %</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[18px] font-bold text-[#B08D57]">{euro(result.gli)}</div>
                      <div className="text-[12px] text-white/50">/mois</div>
                    </div>
                  </div>
                </div>

                <a
                  href="/#contact"
                  className="group mt-7 flex items-center justify-center gap-2 rounded-md bg-[#B08D57] px-6 py-4 text-[15px] font-semibold text-[#26241F] transition-colors hover:bg-[#c19d63]"
                >
                  Confier mon bien à notre équipe
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
                <p className="mt-4 text-center text-[12px] text-white/40">
                  Estimation indicative. Un rendez-vous permet une évaluation précise.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
